import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  FileCheck,
  LineChart,
  Megaphone,
  Wrench,
} from "lucide-react";
import { Eyebrow, MaskLines, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { API, currency, idr } from "@/lib/api";
import { LoopVideo } from "@/components/LoopVideo";
import { IMG } from "@/lib/data";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "An island before the wave",
    text: "Most people who wanted a piece of Bali bought in a decade too late. Lombok runs the same arc eighteen years back: the airport, the circuit, the special economic zone — and beaches still empty at four in the afternoon.",
    img: IMG.coastAerial,
  },
  {
    n: "02",
    title: "Land, villas, full stewardship",
    text: "We source plots with clean certificates, build with local craftsmen, and keep managing the villa after the keys change hands. One counterpart from the first site visit to the nightly rate.",
    img: IMG.poolChairs,
  },
  {
    n: "03",
    title: "Returns you can walk on",
    text: "Managed villa yields of eight to twelve percent a year, and land corridors compounding at fifteen to twenty-five. Published numbers, built on public market data — not brochures.",
    img: IMG.terracesAerial,
  },
];

const SERVICES = [
  {
    icon: FileCheck,
    t: "Licensing, permits & legal",
    d: "PT PMA structuring, freehold and leasehold titles, build permits — handled on the ground.",
  },
  {
    icon: Megaphone,
    t: "Marketing & booking channels",
    d: "Airbnb, Booking.com, direct channels — and the photographers to fill them.",
  },
  {
    icon: Wrench,
    t: "Housekeeping & maintenance",
    d: "Staff, gardens, pools and preventive maintenance. Inspected weekly, reported monthly.",
  },
  {
    icon: LineChart,
    t: "Owner reporting & distributions",
    d: "Occupancy, rate optimisation and transparent accounts, every quarter.",
  },
];

const Card = ({ l, className }) => (
  <Link
    to="/portfolio"
    data-testid={`home-featured-${l.id}`}
    className={`group relative block overflow-hidden bg-ink ${className}`}
  >
    {l.videos?.length ? (
      <LoopVideo
        src={l.videos[0].src}
        poster={l.images[0]}
        testId={`home-featured-video-${l.id}`}
        className="transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
      />
    ) : (
      <img
        src={l.images[0]}
        alt={l.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
    <span className="absolute left-5 top-5 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/90">
      {l.type}
    </span>
    <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/70">
        {l.location}
      </p>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
        <h3 className="font-display text-2xl text-cream md:text-3xl">{l.title}</h3>
        <span className="font-mono text-sm text-gold">
          {l.price_idr_per_are
            ? `${idr(l.price_idr_per_are)} / are`
            : l.price_per_sqm
            ? `from ${currency(l.price_per_sqm)} / m²`
            : currency(l.price_usd)}
        </span>
      </div>
    </div>
  </Link>
);

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 180]);
  const scale = useTransform(scrollY, [0, 900], [1.06, 1.18]);

  return (
    <section className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Villa pool overlooking the Lombok coast"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/20 to-ink/85" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-6 pb-16 md:px-10 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-gold"
          data-testid="hero-coordinates"
        >
          8.6705° S — 116.3329° E · Lombok, Indonesia
        </motion.p>

        <h1 className="font-display text-[clamp(3rem,9vw,8rem)] leading-[0.95] text-cream">
          <MaskLines
            delay={0.45}
            lines={[
              <>Own the island</>,
              <em className="text-gold">before the world</em>,
              <>arrives.</>,
            ]}
          />
        </h1>

        <div className="mt-10 flex flex-wrap items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="max-w-md leading-relaxed text-cream/75"
          >
            Villas and land on Indonesia's next great island — sourced, built
            and managed by people who live here.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="flex flex-wrap gap-3"
          >
            <Link
              to="/portfolio"
              data-testid="hero-cta-portfolio"
              className="group inline-flex items-center gap-3 rounded-full bg-cream px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-gold"
            >
              Explore the portfolio
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/lombok"
              data-testid="hero-cta-lombok"
              className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-cream transition-colors hover:bg-cream/10"
            >
              Discover Lombok
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Chapters() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-36">
      <Reveal>
        <Eyebrow>The Pachamama thesis</Eyebrow>
      </Reveal>
      <div className="mt-14 space-y-20 md:space-y-32">
        {CHAPTERS.map((c, i) => (
          <div
            key={c.n}
            className={`grid items-center gap-10 md:grid-cols-12 md:gap-16 ${
              i % 2 === 1 ? "md:[direction:rtl]" : ""
            }`}
          >
            <div className="md:col-span-5 md:[direction:ltr]">
              <Reveal>
                <span className="font-mono text-sm text-terracotta">{c.n}</span>
                <h2 className="mt-4 font-display text-3xl leading-tight md:text-5xl">
                  {c.title}
                </h2>
                <p className="mt-6 max-w-md leading-relaxed text-moss">{c.text}</p>
              </Reveal>
            </div>
            <Reveal className="md:col-span-7 md:[direction:ltr]" delay={0.1}>
              <div className="group relative overflow-hidden rounded-t-[10rem] md:rounded-t-[12rem]">
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105 md:aspect-[16/10]"
                />
                <span className="pointer-events-none absolute inset-0 rounded-t-[10rem] border border-pine/15 md:rounded-t-[12rem]" />
              </div>
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  );
}

function Management() {
  return (
    <section className="grain relative bg-ink text-cream">
      <div className="relative z-10 mx-auto grid max-w-[1440px] gap-14 px-6 py-24 md:grid-cols-2 md:items-center md:px-10 md:py-36">
        <div>
          <Reveal>
            <Eyebrow light>Ownership, handled</Eyebrow>
            <h2 className="mt-5 font-display text-4xl leading-[1.05] md:text-6xl">
              Property management,
              <br />
              <em className="text-gold">end to end.</em>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-cream/70">
              A villa on an island is a small hotel. We run it like one — so
              owners hold the asset, not the workload.
            </p>
          </Reveal>
          <div className="mt-12 space-y-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div className="flex gap-5 border-b border-cream/10 pb-8">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl">{s.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-cream/60">
                      {s.d}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <div className="relative mx-auto max-w-md md:max-w-none">
            <span className="absolute -left-3 -top-3 h-full w-full rounded-t-full border border-gold/50 md:-left-5 md:-top-5" />
            <img
              src={IMG.infinityCliff}
              alt="Cliffside infinity pool"
              loading="lazy"
              className="relative aspect-[3/4] w-full rounded-t-full object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function InvestorBanner() {
  return (
    <section className="relative overflow-hidden bg-palm text-cream">
      <img
        src={IMG.sunsetCliff}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-palm via-palm/85 to-transparent" />
      <div className="relative z-10 mx-auto flex max-w-[1440px] flex-col items-start gap-8 px-6 py-24 md:flex-row md:items-end md:justify-between md:px-10 md:py-32">
        <Reveal>
          <Eyebrow light>Invest with us</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-4xl leading-[1.05] md:text-6xl">
            Put your capital where the <em className="text-gold">island grows</em>
          </h2>
          <p className="mt-6 max-w-lg leading-relaxed text-cream/75">
            The Investor Premium Club — invest in land, in properties, or
            through our structure, with target returns published up front.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/investor-club"
            data-testid="home-cta-investor-club"
            className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors hover:bg-cream"
          >
            Enter the Investor Premium Club
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetch(`${API}/listings?sort=featured`)
      .then((r) => r.json())
      .then((d) => setFeatured((Array.isArray(d) ? d : []).slice(0, 4)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <main>
      <Hero />
      <Marquee
        items={[
          "Kuta land ×10 since the circuit",
          "18–22% land CAGR 2021–2024",
          "8–12% managed villa yields",
          "Rp 5.74T invested in Mandalika SEZ",
          "Lombok International Airport",
          "30 minutes from Bali",
        ]}
      />
      <Chapters />
      <section className="mx-auto max-w-[1440px] px-6 pb-24 md:px-10 md:pb-36">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Selected work</Eyebrow>
            <h2 className="mt-5 font-display text-4xl md:text-6xl">
              From the portfolio
            </h2>
          </div>
          <Link
            to="/portfolio"
            data-testid="home-cta-all-listings"
            className="group inline-flex items-center gap-2 border-b border-terracotta pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-terracotta"
          >
            View all listings
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
          {featured.length ? (
            featured.map((l, i) => (
              <Card
                key={l.id}
                l={l}
                className={`h-[420px] md:h-[520px] ${
                  featured.length === 1
                    ? "md:col-span-12"
                    : i === 0 || i === 3
                    ? "md:col-span-7"
                    : "md:col-span-5"
                }`}
              />
            ))
          ) : (
            <div className="h-[420px] md:col-span-12 md:h-[520px] flex items-center justify-center bg-sand font-mono text-xs uppercase tracking-[0.2em] text-moss" data-testid="home-featured-loading">
              Curating the portfolio…
            </div>
          )}
        </div>
      </section>
      <Management />
      <InvestorBanner />
    </main>
  );
}
